import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { goalsApi, usersApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Pencil, Trash2, Target, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatCurrency, getStatusColor, getHorizonColor, getDepartmentColor } from '../lib/utils';
import { computeAllowedEndDate, isFixedDurationCycle, validateDateRange } from '../lib/dateUtils';

const DEPARTMENTS = ["Marketing", "Sales", "Operations", "Finance", "R&D", "HR/People", "Admin"];
const HORIZONS = ["Weekly", "15-Day", "30-Day", "35-Day", "45-Day", "Monthly", "6-Month", "1-Year"];
const METRIC_TYPES = ["Currency", "Count", "Percent", "Yes-No"];
const STATUSES = ["Active", "Completed", "Paused", "Draft"];

export default function Goals() {
  const { isAdmin, isAdminOrFounder, canDelete } = useAuth();
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [filterHorizon, setFilterHorizon] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [goalsRes, usersRes] = await Promise.all([
        goalsApi.getAll(),
        usersApi.getMembers()
      ]);
      setGoals(goalsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load goals');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;
    try {
      await goalsApi.delete(id);
      setGoals(goals.filter(g => g.id !== id));
      toast.success('Goal deleted');
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const filteredGoals = goals.filter(goal => {
    if (filterHorizon !== 'all' && goal.horizon !== filterHorizon) return false;
    if (filterStatus !== 'all' && goal.status !== filterStatus) return false;
    return true;
  });

  const getUserName = (userId) => {
    if (!userId) return null; // Return null for empty/undefined owner
    const user = users.find(u => u.id === userId);
    return user ? user.name : 'Unknown User';
  };

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 skeleton rounded-lg" />)}</div>;
  }

  return (
    <div className="space-y-6" data-testid="goals-page">
      <div className="flex items-center justify-between">
        <div className="page-header mb-0">
          <h1 className="page-title">{isAdminOrFounder() ? 'All Goals' : 'My Goals'}</h1>
          <p className="page-subtitle">Track and manage performance goals</p>
        </div>
        {isAdminOrFounder() && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) setEditingGoal(null); // Clear editing state when dialog closes
          }}>
            <DialogTrigger asChild>
              <Button onClick={() => setEditingGoal(null)} data-testid="create-goal-btn">
                <Plus className="w-4 h-4 mr-2" /> Create Goal
              </Button>
            </DialogTrigger>
            <GoalDialog
              key={editingGoal?.id || 'new'}
              goal={editingGoal}
              users={users}
              onClose={() => {
                setDialogOpen(false);
                setEditingGoal(null); // Clear editing state
              }}
              onSave={() => {
                loadData();
                setEditingGoal(null); // Clear editing state after save
              }}
            />
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={filterHorizon} onValueChange={setFilterHorizon}>
          <SelectTrigger className="w-40" data-testid="filter-horizon">
            <SelectValue placeholder="All Horizons" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Horizons</SelectItem>
            {HORIZONS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40" data-testid="filter-status">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Goals Table */}
      <Card className="border border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Goal</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Horizon</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-28">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredGoals.length > 0 ? filteredGoals.map(goal => (
                <TableRow key={goal.id} className={goal.status === 'Draft' ? 'bg-amber-50/50' : ''}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {goal.title}
                      {goal.status === 'Draft' && (
                        <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
                          Needs target
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={getDepartmentColor(goal.department)}>
                      {goal.department}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={getHorizonColor(goal.horizon)}>{goal.horizon}</Badge>
                  </TableCell>
                  <TableCell>
                    {goal.metric_type === 'Yes-No' ? (
                      <span className="text-slate-500">Yes/No</span>
                    ) : goal.target_value > 0 ? (
                      formatCurrency(goal.target_value, goal.unit)
                    ) : (
                      <span className="text-amber-600">Not set</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {goal.metric_type === 'Yes-No' ? (
                      <Badge variant="outline" className={goal.status === 'Completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50'}>
                        {goal.status === 'Completed' ? 'Done' : 'Pending'}
                      </Badge>
                    ) : goal.target_value > 0 ? (
                      <div className="flex items-center gap-2">
                        <Progress value={goal.progress || 0} className="w-20 h-2" />
                        <span className="text-sm">{Math.round(goal.progress || 0)}%</span>
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400">N/A</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {getUserName(goal.owner_id) ? (
                      getUserName(goal.owner_id)
                    ) : (
                      <span className="text-red-500">Not assigned</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={getStatusColor(goal.status)}>
                      {goal.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/goals/${goal.id}`)}
                        data-testid={`view-goal-${goal.id}`}
                        title="View Details"
                      >
                        <Eye className="w-4 h-4 text-blue-500" />
                      </Button>
                      {isAdminOrFounder() && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setEditingGoal(goal); setDialogOpen(true); }}
                          data-testid={`edit-goal-${goal.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      {canDelete() && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(goal.id)}
                          data-testid={`delete-goal-${goal.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                    <Target className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    No goals found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function GoalDialog({ goal, users, onClose, onSave }) {
  const getInitialForm = (goalData) => ({
    title: goalData?.title || '',
    department: goalData?.department || 'Sales',
    horizon: goalData?.horizon || 'Weekly',
    metric_type: goalData?.metric_type || 'Currency',
    target_value: goalData?.target_value || 0,
    unit: goalData?.unit || '₹',
    start_date: goalData?.start_date || new Date().toISOString().split('T')[0],
    end_date: goalData?.end_date || '',
    owner_id: goalData?.owner_id || '',
    status: goalData?.status || 'Active',
  });

  const [form, setForm] = useState(getInitialForm(goal));
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [maxEndDate, setMaxEndDate] = useState('');

  // Re-hydrate form state when goal changes (edit vs create mode)
  useEffect(() => {
    if (goal?.id) {
      // Edit mode: populate form with goal data
      setForm(getInitialForm(goal));
    } else {
      // Create mode: reset to clean default
      setForm(getInitialForm(null));
    }
    setValidationError('');
  }, [goal?.id]);

  // Update max end date when start_date or horizon changes
  useEffect(() => {
    if (form.start_date && form.horizon) {
      const allowed = computeAllowedEndDate(form.start_date, form.horizon);
      setMaxEndDate(allowed);
      
      // For fixed duration cycles, auto-fill end date
      if (isFixedDurationCycle(form.horizon)) {
        setForm(prev => ({ ...prev, end_date: allowed }));
      } else {
        // For variable cycles, clear end date if it's beyond allowed
        if (form.end_date && form.end_date > allowed) {
          setForm(prev => ({ ...prev, end_date: '' }));
        }
      }
    }
  }, [form.start_date, form.horizon]);

  const handleHorizonChange = (newHorizon) => {
    const newForm = { ...form, horizon: newHorizon };
    if (form.start_date) {
      const allowed = computeAllowedEndDate(form.start_date, newHorizon);
      if (isFixedDurationCycle(newHorizon)) {
        newForm.end_date = allowed;
      } else {
        // Keep current end date only if it's within allowed range
        if (form.end_date && form.end_date > allowed) {
          newForm.end_date = '';
        }
      }
    }
    setForm(newForm);
  };

  const handleStartDateChange = (newStartDate) => {
    const newForm = { ...form, start_date: newStartDate };
    if (newStartDate && form.horizon) {
      const allowed = computeAllowedEndDate(newStartDate, form.horizon);
      if (isFixedDurationCycle(form.horizon)) {
        newForm.end_date = allowed;
      } else {
        // Clear end date if it's now invalid
        if (form.end_date && form.end_date > allowed) {
          newForm.end_date = '';
        }
      }
    }
    setForm(newForm);
  };

  const validateForm = () => {
    // Owner is required
    if (!form.owner_id) {
      return 'Owner is required';
    }
    
    // For Currency/Count/Percent, target must be > 0
    if (['Currency', 'Count', 'Percent'].includes(form.metric_type)) {
      if (!form.target_value || form.target_value <= 0) {
        return `Target value must be greater than 0 for ${form.metric_type} metric type`;
      }
    }
    
    // Validate date range
    if (form.start_date && form.end_date && form.horizon) {
      const validation = validateDateRange(form.start_date, form.end_date, form.horizon);
      if (!validation.isValid) {
        return validation.error;
      }
    }
    
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const error = validateForm();
    if (error) {
      setValidationError(error);
      toast.error(error);
      return;
    }
    
    setValidationError('');
    setLoading(true);
    try {
      if (goal && goal.id) {
        await goalsApi.update(goal.id, form);
        toast.success('Goal updated');
      } else {
        await goalsApi.create(form);
        toast.success('Goal created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save goal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>{goal ? 'Edit Goal' : 'Create Goal'}</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label>Title</Label>
          <Input
            value={form.title}
            onChange={e => setForm({...form, title: e.target.value})}
            placeholder="Enter goal title"
            required
            data-testid="goal-title-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Department</Label>
            <Select value={form.department} onValueChange={v => setForm({...form, department: v})}>
              <SelectTrigger data-testid="goal-department-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Horizon</Label>
            <Select value={form.horizon} onValueChange={handleHorizonChange}>
              <SelectTrigger data-testid="goal-horizon-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HORIZONS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Metric Type</Label>
            <Select value={form.metric_type} onValueChange={v => {
              setForm({...form, metric_type: v, target_value: v === 'Yes-No' ? 0 : form.target_value});
            }}>
              <SelectTrigger data-testid="goal-metric-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METRIC_TYPES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.metric_type !== 'Yes-No' && (
            <>
              <div className="space-y-2">
                <Label>Target Value <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  value={form.target_value}
                  onChange={e => setForm({...form, target_value: parseFloat(e.target.value) || 0})}
                  min="1"
                  required
                  data-testid="goal-target-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Select value={form.unit} onValueChange={v => setForm({...form, unit: v})}>
                  <SelectTrigger data-testid="goal-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="₹">₹ (INR)</SelectItem>
                    <SelectItem value="count">Count</SelectItem>
                    <SelectItem value="%">Percent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          {form.metric_type === 'Yes-No' && (
            <div className="col-span-2 flex items-center text-sm text-slate-500 pt-6">
              Yes-No goals track completion status only (Done/Not Done)
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Start Date</Label>
            <Input
              type="date"
              value={form.start_date}
              onChange={e => handleStartDateChange(e.target.value)}
              required
              data-testid="goal-start-date"
            />
          </div>
          <div className="space-y-2">
            <Label>End Date {isFixedDurationCycle(form.horizon) && <span className="text-xs text-slate-500">(auto)</span>}</Label>
            <Input
              type="date"
              value={form.end_date}
              onChange={e => setForm({...form, end_date: e.target.value})}
              min={form.start_date}
              max={maxEndDate}
              required
              disabled={isFixedDurationCycle(form.horizon)}
              className={isFixedDurationCycle(form.horizon) ? 'bg-slate-100' : ''}
              data-testid="goal-end-date"
            />
            {maxEndDate && (
              <p className="text-xs text-slate-500">
                Max allowed: {maxEndDate}
              </p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Owner</Label>
            <Select value={form.owner_id} onValueChange={v => setForm({...form, owner_id: v})}>
              <SelectTrigger data-testid="goal-owner-select">
                <SelectValue placeholder="Select owner" />
              </SelectTrigger>
              <SelectContent>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
              <SelectTrigger data-testid="goal-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={loading} data-testid="save-goal-btn">
            {loading ? 'Saving...' : 'Save Goal'}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
}
